import React from "react";
import { Link } from "react-router-dom";
import { Mail, Globe, Shield } from "lucide-react";

export const SUPPORT_EMAIL = "reachus@adukoo.com";
export const PROD_DOMAINS = ["adukoo.com", "adukoo.in"];

/**
 * v79.209 — Founder direction (Feb 2026): "Hide footer buttons on all
 * pages. The footer section has a lot of empty spaces — sort and align
 * the content accordingly." Previous toggle-based footer (show/hide)
 * created its own visual noise (yellow pill overlapping HelperBot z-
 * index) AND left ~200-300 px of empty navy space between columns on
 * every desktop view.
 *
 * The rewrite ditches every button, ditches the accordion + collapsed
 * state, and lays every legal/brand touchpoint as one tight strip that
 * mobile-wraps naturally:
 *   Row 1  Brand (logo · tagline · support · domains)
 *   Row 2  Legal quick-links (About · Terms · Privacy · Contact · Report)
 *   Row 3  Trust + Affiliate disclosures (1-line inline)
 *   Row 4  Country strip (single-line horizontal chip scroll)
 *   Row 5  Copyright + trademark
 *
 * Result: ~40 % vertical reduction on desktop, no wasted whitespace, no
 * toggle button, no z-index fights with HelperBot / RefreshFAB, no
 * localStorage state to keep in sync.
 */

// Rendered inline as chips inside the country strip. Ordered by the
// countries where Adukoo has the most active listings today.
const COUNTRY_CHIPS = [
    ["us", "United States"], ["in", "India"], ["gb", "United Kingdom"],
    ["ca", "Canada"], ["au", "Australia"], ["ae", "UAE"],
    ["sa", "Saudi Arabia"], ["sg", "Singapore"], ["my", "Malaysia"],
    ["de", "Germany"], ["fr", "France"], ["it", "Italy"],
    ["es", "Spain"], ["nl", "Netherlands"], ["jp", "Japan"],
    ["br", "Brazil"], ["mx", "Mexico"], ["za", "South Africa"],
    ["ng", "Nigeria"], ["ke", "Kenya"], ["eg", "Egypt"],
    ["pk", "Pakistan"], ["bd", "Bangladesh"], ["lk", "Sri Lanka"],
    ["np", "Nepal"], ["id", "Indonesia"], ["ph", "Philippines"],
    ["th", "Thailand"], ["vn", "Vietnam"], ["tr", "Türkiye"],
    ["pl", "Poland"], ["se", "Sweden"], ["no", "Norway"],
    ["dk", "Denmark"], ["ie", "Ireland"], ["ch", "Switzerland"],
    ["be", "Belgium"], ["pt", "Portugal"], ["gr", "Greece"],
];

const LEGAL_LINKS = [
    // v79.228 — Removed duplicate `/help` entry (same page as How it works).
    // Restored `/investors` — was inadvertently dropped from the tight
    // footer refactor; founder confirmed it should stay visible.
    // v79.230 — Removed `/for-businesses`, `/refund-policy`, `/cookies`
    // and `/contact` from the footer. None of these have real routes in
    // App.js — they were silently falling through to the "*" catch-all
    // and dumping visitors on the Home page. Rather than build 4 empty
    // pages just to satisfy the footer, we drop the broken links until
    // real content exists. Terms & Privacy (which DO have pages) stay.
    // Support-email + Report Impersonation mailto links already live in
    // the top strip of the footer, so users still have a Contact route.
    { to: "/how-it-works", label: "How it works" },
    { to: "/about", label: "About" },
    { to: "/guides", label: "Guides" },
    { to: "/investors", label: "Investors" },
    { to: "/terms", label: "Terms" },
    { to: "/privacy", label: "Privacy" },
];

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid="aduku-footer"
            data-collapsed="0"
            className="bg-aduku-navy text-white/90 relative z-10 border-t border-aduku-navy-800"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4 space-y-2.5">
                {/* Row 1 — Brand + support (single line on desktop, wraps on mobile) */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4">
                    <Link
                        to="/"
                        aria-label="Adukoo — Worldwide marketplace."
                        className="inline-flex items-baseline gap-2"
                        data-protected="true"
                        translate="no"
                    >
                        <span className="font-display font-black tracking-tight text-aduku-yellow text-2xl sm:text-3xl notranslate">
                            Adukoo<sup className="text-[10px] sm:text-xs align-super ml-0.5">™</sup>
                        </span>
                        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/85 font-extrabold notranslate">Worldwide marketplace</span>
                    </Link>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                        <a
                            data-testid="footer-support-email"
                            href={`mailto:${SUPPORT_EMAIL}?subject=Adukoo%20Support`}
                            className="inline-flex items-center gap-1.5 text-white/90 hover:text-aduku-yellow"
                        >
                            <Mail className="w-3.5 h-3.5" />
                            <span className="break-all">{SUPPORT_EMAIL}</span>
                        </a>
                        {PROD_DOMAINS.map((d) => (
                            <a
                                key={d}
                                data-testid={`footer-website-${d}`}
                                href={`https://www.${d}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-white/80 hover:text-aduku-yellow"
                            >
                                <Globe className="w-3.5 h-3.5" /> www.{d}
                            </a>
                        ))}
                    </div>
                </div>

                {/* Row 2 — Legal quick-links (single wrap-friendly row) */}
                <nav
                    data-testid="footer-legal-links"
                    className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] font-semibold text-white/80"
                >
                    {LEGAL_LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`footer-link-${l.to.replace("/", "")}`}
                            className="hover:text-aduku-yellow"
                        >
                            {l.label}
                        </Link>
                    ))}
                    <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=Report%20Impersonation`}
                        className="hover:text-aduku-yellow"
                        data-testid="footer-link-report-impersonation"
                    >
                        Report Impersonation
                    </a>
                </nav>

                {/* Row 3 — Trust + Affiliate disclosures (single line each) */}
                <div className="space-y-1.5 text-[11px] leading-snug">
                    <div
                        data-testid="footer-trust-band"
                        className="flex items-start gap-1.5 text-white/80"
                    >
                        <Shield className="w-3.5 h-3.5 text-aduku-yellow shrink-0 mt-[1px]" />
                        <span>
                            <b className="text-aduku-yellow">Official Adukoo<sup>™</sup>.</b> Only <a href="https://www.adukoo.com" target="_blank" rel="noopener noreferrer" className="text-aduku-yellow hover:underline">adukoo.com</a>, <a href="https://www.adukoo.in" target="_blank" rel="noopener noreferrer" className="text-aduku-yellow hover:underline">adukoo.in</a> and the Google Play Store (publisher <b>Adukoo Tech</b>) are legitimate.
                        </span>
                    </div>
                    <div
                        data-testid="footer-affiliate-disclosure"
                        className="text-white/65"
                    >
                        <b className="text-aduku-yellow">Sponsored disclosure:</b> Listings marked <b>Sponsored</b> are affiliate referral links. Adukoo or the lister may earn a commission at no extra cost to you.
                    </div>
                </div>

                {/* Row 4 — Country strip (compact horizontal scroll) */}
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-1.5">Browse by country</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/70">
                        {COUNTRY_CHIPS.map(([cc, name]) => (
                            <Link
                                key={cc}
                                to={`/c/${cc}`}
                                data-testid={`footer-country-${cc}`}
                                className="hover:text-aduku-yellow whitespace-nowrap"
                            >
                                {name}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Row 5 — Copyright + trademark (v79.218: merged into one
                    tight sentence so we don't leave a giant flex-between
                    gap on wide desktops between the © and the trademark
                    warning). */}
                <div className="pt-2 border-t border-aduku-navy-800 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-white/70 text-center">
                    <Shield className="w-3 h-3 text-aduku-yellow" />
                    <span data-testid="footer-copyright" data-protected="true">
                        © {year} <b className="text-white">Adukoo Tech</b><sup>™</sup>. All rights reserved.
                    </span>
                    <span className="text-white/40 hidden sm:inline">·</span>
                    <span className="text-white/55">Adukoo™ is a trademark of Adukoo Tech. Unauthorised use prohibited.</span>
                </div>
            </div>
        </footer>
    );
}
